import {
  Assessment,
  Descendant,
  EditorDraft,
  EditorVersion,
  getMostRecentAssessment,
  getShortcutArgs,
  PopulatedNode,
  SubmitAssessmentResultsArgs,
  TemplateId,
  TemplateKey,
  UpdateCallback,
  WithId,
} from '@hb/shared'
import { forwardRef, PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react'
import {
  saveAssessmentDraft,
  submitAssessmentResults,
} from '../../../backend/functions/assessments'
import { useProfile } from '../../../contexts/user/ProfileContext'
import { usePopulatedAssessment } from '../../../hooks/backend/user/usePopulatedAssessment'
import { useAssessmentAutoTemplate } from '../hooks/useAssessmentAutoTemplate'
import { AssessmentResultsProvider } from './assessmentResults'
import { EditMode, TemplateDataProvider } from './data'
import { EditModeProvider } from './editMode'
import { useTemplateInsurancePlans } from './plan'
import { TemplateViewProvider, TemplateViewRef } from './view'

export interface AssessmentDataProviderProps {
  type: TemplateKey
  editMode: EditMode
}
export const AssessmentDataProvider = ({
  children,
  type,
  editMode,
}: PropsWithChildren<AssessmentDataProviderProps>) => {
  const { selectedPlan } = useTemplateInsurancePlans()
  const { selectedAssessment, assessmentId, user, assessments } = useProfile()

  const mostRecentAssessment = useMemo(
    () => getMostRecentAssessment(assessments ?? {}),
    [assessments],
  )
  const usedAssessment = useMemo<WithId<Assessment> | null>(() => {
    if (assessmentId) {
      const data = assessments?.[assessmentId] ?? null
      return data ? { ...data, id: assessmentId } : null
    }
    return mostRecentAssessment
  }, [assessmentId, assessments, mostRecentAssessment])
  const toBePopulated = useMemo(
    () =>
      !!mostRecentAssessment && mostRecentAssessment.id !== assessmentId
        ? mostRecentAssessment
        : null,
    [mostRecentAssessment, assessmentId],
  )

  const mostRecentPopulated = usePopulatedAssessment(toBePopulated, user)
  const populatedAssessment = useMemo(() => {
    if (assessmentId) return selectedAssessment.populated
    return mostRecentPopulated
  }, [selectedAssessment, mostRecentPopulated, assessmentId])

  const shortcutArgs = useMemo(
    () => getShortcutArgs(user, usedAssessment?.id ?? null, populatedAssessment, selectedPlan),
    [user, selectedPlan, populatedAssessment, usedAssessment],
  )

  return (
    <EditModeProvider editMode={editMode}>
      <TemplateDataProvider type={type} data={shortcutArgs}>
        {children}
      </TemplateDataProvider>
    </EditModeProvider>
  )
}

type AssessmentResultsArgs = Omit<
  SubmitAssessmentResultsArgs,
  'consentFormIds' | 'viewableBeforeCall'
>
export const AssessmentEditorProvider = forwardRef<
  TemplateViewRef,
  PropsWithChildren<{ draft?: EditorDraft | null }>
>(({ children, draft }, ref) => {
  const {
    assessmentId,
    selectedAssessment: { populated: assessment },
    user,
  } = useProfile()

  const { selectedPlanId } = useTemplateInsurancePlans()
  const [confirmingConsentFormsForResults, setConfirmingConsentFormsForResults] =
    useState<AssessmentResultsArgs | null>(null)
  const onConfirmSuccess = useRef<((result: UpdateCallback) => void) | null>(null)
  const onConfirmError = useRef<((reason: Error) => void) | null>(null)

  const onConfirmAssessmentConfig = useCallback(
    async (consentFormIds: TemplateId[], viewableBeforeCall: boolean) => {
      if (!assessmentId) return { error: 'Internal error - no assessment id' }
      if (!confirmingConsentFormsForResults)
        return { error: 'Internal error - not confirming consent forms for results' }
      try {
        const result = await submitAssessmentResults({
          ...confirmingConsentFormsForResults,
          consentFormIds,
          viewableBeforeCall,
        })
        // Resolve the stored promise with the result
        if (onConfirmSuccess.current) {
          onConfirmSuccess.current(result.data as UpdateCallback)
        }
        // Clear the stored promise and state
        onConfirmSuccess.current = null
        onConfirmError.current = null
        setConfirmingConsentFormsForResults(null)
        return result.data as UpdateCallback
      } catch (err: any) {
        // Reject the stored promise with the error
        console.error(err)
        if (onConfirmError.current) {
          onConfirmError.current(new Error(err.message ?? 'Error submitting assessment results'))
        }
        // Clear the stored promise and state
        onConfirmError.current = null
        onConfirmSuccess.current = null
        setConfirmingConsentFormsForResults(null)
        return { error: err.message } as UpdateCallback
      }
    },
    [assessmentId, confirmingConsentFormsForResults],
  )

  const onCancelConsentFormsForResults = useCallback(() => {
    if (onConfirmSuccess.current) {
      onConfirmSuccess.current({ success: 'Cancelled sending assessment' })
    }
    onConfirmSuccess.current = null
    onConfirmError.current = null
    setConfirmingConsentFormsForResults(null)
  }, [])

  const onSubmit = useCallback(
    async (text: PopulatedNode[], version: EditorVersion) => {
      if (!assessmentId) return { error: 'internal error' }

      // Set the state to trigger the consent form modal
      setConfirmingConsentFormsForResults({
        assessmentId,
        results: text,
        editorVersion: version,
        coverageId: selectedPlanId,
      })

      // Create and store a new promise that will be resolved when consent forms are confirmed
      return new Promise<UpdateCallback>((resolve, reject) => {
        onConfirmSuccess.current = resolve
        onConfirmError.current = reject
      })
    },
    [assessmentId, selectedPlanId],
  )

  const onSave = useCallback(
    async (value: Descendant[], version: EditorVersion) => {
      if (!assessmentId) return { error: 'internal error' }
      return saveAssessmentDraft({
        assessmentId,
        editorVersion: version,
        draft: value,
      })
        .then(res => res.data as UpdateCallback)
        .catch(err => ({ error: err.message }))
    },
    [assessmentId],
  )

  const autoTemplateId = useAssessmentAutoTemplate(assessment)

  return (
    <TemplateViewProvider
      ref={ref}
      autoTemplateId={autoTemplateId}
      type="assessments"
      draft={draft}
      onSave={onSave}
      onSubmit={onSubmit}
      user={user}>
      <AssessmentResultsProvider
        confirmingConsentFormsForResults={confirmingConsentFormsForResults}
        onConfirmAssessmentConfig={onConfirmAssessmentConfig}
        onCancelConsentFormsForResults={onCancelConsentFormsForResults}>
        <AssessmentDataProvider editMode="document" type="assessments">
          {children}
        </AssessmentDataProvider>
      </AssessmentResultsProvider>
    </TemplateViewProvider>
  )
})
